/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React, { lazy, Suspense } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { isEqual } from 'lodash'
import * as Sentry from '@sentry/browser'

import {
  Button,
  SvgIcon,
  Select,
  Input,
  CurrencyInput,
  TextArea,
  CheckboxToggle,
  LabelledDetail,
  FileUpload,
  Grid,
  Spinner
} from '../'

import {
  encodeBase64,
  decodeBase64,
  runQuery,
  getDateUTC,
  getFileExtension
} from '../../utils'

import { MUTATION_REQUESTRUN } from '../../mutations'

import { FILETYPE_MANUAL_UPLOAD } from '../../constants'

const parseRequestRunContent = (content) => {
  let parsedContent = JSON.parse(content)
  for (const [key, properties] of Object.entries(parsedContent)) {
    if (properties.field_type === 'BooleanField' && properties.value === null) {
      parsedContent[key].value = false  // sets boolean fields' value to false by default
    }
  }
  return parsedContent
}

const initialiseContent = (content) => {
  if (content) {
    return parseRequestRunContent(content)
  } else {
    return {}
  }
}


class RequestCaptureForm extends React.PureComponent {
  static propTypes = {
    requestRun: PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      requesttype: PropTypes.object.isRequired,
      identity: PropTypes.object.isRequired,
      sources: PropTypes.array.isRequired,
      status: PropTypes.string.isRequired,
      authorisedAt: PropTypes.string
    }).isRequired,
    requestType: PropTypes.string,
    updateRequestRun: PropTypes.func.isRequired,
    saveProgress: PropTypes.func.isRequired,
    editAllowed: PropTypes.bool,
    className: PropTypes.string
  }

  static getDerivedStateFromProps(props, state) {
    if (props.requestType !== state.requestType) {
      return {
        content: initialiseContent(props.requestRun.requesttype.content),
        requestType: props.requestType,
        fieldInputsToTrimOnSave: [],
        captureComplete: props.requestRun.isCaptured,
        saveInProgress: false,
        saveAndMarkCompleteInProgress: false
      }
    } else {
      return null
    }
  }

  state = {
    content: initialiseContent(this.props.requestRun.content),
    requestType: this.props.requestType,
    fieldInputsToTrimOnSave: [],
    captureComplete: this.props.requestRun.isCaptured,
    saveInProgress: false,
    saveAndMarkCompleteInProgress: false,
    authorisedAt: this.props.requestRun.authorisedAt
  }

  requiredFieldsBeenCaptured = () => {
    let requiredFieldsNotSet = Object.entries(this.state.content).filter(([field, data]) => (
      data.required && (data.value === null || data.value === '')
    ))

    return requiredFieldsNotSet.length === 0 && (
      this.props.requestRun.requesttype.authorisationRequired
        ? this.state.authorisedAt !== null
        : true
    )
  }

  anyFieldChanged = () => {
    let requestRun = this.props.requestRun
    let parsedContent = parseRequestRunContent(requestRun.content)

    return !isEqual(parsedContent, this.state.content) || (
      (requestRun.authorisedAt && !moment(requestRun.authorisedAt).isSame(this.state.authorisedAt, 'day')) ||
      (requestRun.authorisedAt === null && this.state.authorisedAt !== null)
    )
  }

  checkAndTrimAnyCharFieldInputs = () => {
    // trim any fields of type Charfield if they were given values by user input
    if (this.state.fieldInputsToTrimOnSave.length > 0) {
      this.state.fieldInputsToTrimOnSave.forEach(field => {
        let contentField = this.state.content[field]
        if (contentField.value !== null && contentField.value.includes(' ')) {
          this.setContent(field, contentField.value.trim())
        }
      })
    }
  }

  updateRequestRun = (captureComplete = false) => {
    const requestRun = this.props.requestRun
    const user = localStorage.getItem('userNodeId')

    let input = {
      id: requestRun.id,
      content: JSON.stringify(this.state.content)
    }

    if (this.state.authorisedAt) {
      input.authorisedAt = this.state.authorisedAt
    }
    if (captureComplete) {
      input.capturedBy = user
    }

    this.props.updateRequestRun(input)
    .then(({ data }) => {
      if (data.requestrunMutate.requestrun.isCaptured && captureComplete) {
        this.setCaptureAsComplete(this.props.saveProgress)
      }

      this.setState(captureComplete
        ? { saveAndMarkCompleteInProgress: false }
        : { saveInProgress: false }
      )
    })
    .catch(error => {
      Sentry.captureMessage(`Failed to update request run when capturing - ${error}`)
      this.setState(captureComplete
        ? { fieldInputsToTrimOnSave: [], saveAndMarkCompleteInProgress: false }
        : { fieldInputsToTrimOnSave: [], saveInProgress: false }
      )
    })
  }

  uploadSelectedFile = (id, field) => {
    this.setState({ fileUploadInProgress: true })

    let file = document.getElementById(id).files[0]
    let reader = new FileReader()
    let headers = new Headers({
      'Authorization': 'Token ' + localStorage.getItem('token') || null
    })

    let data = new FormData()
    data.append('file', file)
    data.append('filetype', FILETYPE_MANUAL_UPLOAD)  // this will get updated in associated workflow
    data.append('relationships', JSON.stringify([
      ['requestrun', decodeBase64(this.props.requestRun.id)]
    ]))

    reader.onload = (e) => {
      fetch(`${process.env.REACT_APP_API_ROOT}/v1/file/`, {
        method: 'post',
        headers: headers,
        body: data
      })
      .then(response => {
        if (response.ok) {  // covers 200-range of http responses
          console.info("File uploaded")
          response.json()
            .then(data => {
              this.setState({
                fileUploadInProgress: false
              })
              this.setContent(field, decodeBase64(data.file))
            })
        } else {
          console.error("File upload failed")
          this.setState({ fileUploadInProgress: false })
        }
      })
      .catch(error => {
        console.error(`File upload failed - ${error}`)
        this.setState({ fileUploadInProgress: false })
      })
    }

    reader.readAsText(file)
  }

  setCaptureAsComplete = (saveProgress) => (
    this.setState(
      { captureComplete: true, fieldInputsToTrimOnSave: [] },
      saveProgress
    )
  )

  setContent = (field, value) => {
    let content = Object.assign({}, this.state.content)
    content[field].value = value
    this.setState({ content })
  }

  getInput = (field, value) => {
    if (value.hasOwnProperty('choices')) {
      let choices = [{ label: '', value: null }].concat(value.choices)
      return (
        <Select
          id={field}
          placeholder='Choose'
          value={this.state.content[field].value || null}
          options={choices}
          onChange={e => this.setContent(field, e.target.value)}
          className='slds-size_2-of-3'
        />
      )
    }

    switch(value.field_type) {
      case "TextField":
        return (
          <TextArea
            id={field}
            title={value.help_text}
            placeholder={value.label}
            value={this.state.content[field].value || ''}
            onChange={e => this.setContent(field, e.target.value)}
            className='slds-size_1-of-1'
          />
        )

      case "DecimalField":
        return (
          <CurrencyInput
            value={this.state.content[field].value || ''}
            onChange={e => this.setContent(field, e.target.value === '' ? null : e.target.value)}
            className='slds-size_3-of-4'
          />
        )

      case "DateField":
        return (
          <DatePicker
            selected={this.state.content[field].value
              ? moment(this.state.content[field].value)
              : null
            }
            onChange={date => (
              this.setContent(field, date ? getDateUTC(date).format('YYYY-MM-DD') : null)
            )}
            dateFormat='ll'
            inline={true}
            showMonthDropdown={true}
            showYearDropdown={true}
          />
        )

      case "BooleanField":
        return (
          <CheckboxToggle
            id={field}
            checked={this.state.content[field].value}
            onChange={() => this.setContent(field, !this.state.content[field].value)}
          />
        )

      case "CharField":
        if (!this.state.fieldInputsToTrimOnSave.includes(field)) {
          let fieldInputsToTrimOnSave = Object.assign([], this.state.fieldInputsToTrimOnSave)
          fieldInputsToTrimOnSave.push(field)
          this.setState({ fieldInputsToTrimOnSave })
        }
        return (
          <Input
            id={field}
            title={value.help_text}
            placeholder={value.label}
            value={this.state.content[field].value || ''}
            required={value.required}
            onChange={e => this.setContent(field, e.target.value)}
            className='slds-size_2-of-3'
          />
        )

      case "FileField":
        const id = `fileupload_${field}`
        return (
          <FileUpload
            id={id}
            endpoint='/v1/file/'
            onFileSelect={(e) => this.uploadSelectedFile(id, field)}
            noUploadButton={true}
            sldsStyle={true}
            disabled={value.disabled}  // this should only ever be the case if the file is expected to be provided by employee/identity
            key={field}
          />
        )

      case "CustomField":
        const DynamicComponent = lazy(() => import(`./${value.component}`))
        return (
          <Suspense fallback={<Spinner size='small' />}>
            <DynamicComponent requestRun={this.props.requestRun} />
          </Suspense>
        )

      default:
        return null
    }
  }

  // NOTE: alternative to using below is to use Apollo Query's run delay
  queryFileRelations = () => (
    runQuery(
      // model
      'filerelations',
      // parameters
      [
        { name: 'objectId', type: 'UUID', value: decodeBase64(this.props.requestRun.identity.id) },
        // { name: 'file_Filetype', type: 'UUID', value: decodeBase64(FILETYPE_FORMS) },
        { name: 'orderBy', type: 'String!', value: '-created_at'},
        { name: 'first', type: 'Int', value: 1 }
      ],
      null,
      // fields to return
      `edges {
        node {
          id
          file {
            id
            asset
            children {
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }`
    )
  )

  getReadOnlyDetailValue = (field, { field_type, value }) => {
    if (field_type === 'FileField') {
      if (value === null) {
        return '-'
      }

      return (
        <Query
          query={gql`
            query($id: ID!) {
              file(id: $id) {
                id
                asset
                url
              }
            }`
          }
          variables={{ id: encodeBase64(this.state.content[field].value, 'FileNode') }}>

          {({ loading, error, data }) => {
            if (loading) {
              return <Spinner type='tail' />
            }

            if (error) {
              return (
                <div className='slds-size_1-of-1 slds-align_absolute-center' style={{ height: '150px', flexDirection: 'column' }}>
                  <SvgIcon
                    variant='utility'
                    symbol='image'
                    size='x-large'
                    colour='error'
                    className='slds-m-bottom_x-small slds-text-align_center'
                  />
                  Failed to fetch preview file
                </div>
              )
            }

            if (data.file === null) {
              return (
                <div className='slds-size_1-of-1 slds-align_absolute-center' style={{ height: '150px', flexDirection: 'column' }}>
                  <SvgIcon
                    variant='utility'
                    symbol='image'
                    size='x-large'
                    colour='warning'
                    className='slds-m-bottom_x-small slds-text-align_center'
                  />
                  No file found to preview
                </div>
              )
            }

            const fileExtension = getFileExtension(data.file.asset)
            const imageFormats = ['png', 'jpg', 'jpeg']

            if (imageFormats.indexOf(fileExtension) > -1) {
              return (
                <figure className='slds-file'>
                  <img
                    src={data.file.url}
                    alt={data.file.asset}
                    style={{ width: '97%' }}
                  />
                  {/* <FileCaption file={data.file} /> */}
                </figure>
              )
            }
            else {
              return 'Unsupported file format'
            }
          }}
        </Query>
      )
    } else if (field_type === 'BooleanField') {
      return this.state.content[field].value ? 'Yes' : 'No'
    } else if (this.state.content[field].hasOwnProperty('choices')) {
      return this.state.content[field].value
        ? this.state.content[field].choices.find(choice => choice.value === this.state.content[field].value).label
        : '-'
    } else {
      return this.state.content[field].value || '-'
    }
  }

  setAuthorisedAtDate = (date) => this.setState({ authorisedAt: getDateUTC(date).format('YYYY-MM-DD') })

  getForm = (sortedContentEntries, requestRun) => {
    if (this.state.captureComplete || requestRun.status === 'ON_HOLD') {
      return (
        <React.Fragment>
          {requestRun.requesttype.authorisationRequired &&
            <LabelledDetail
              label='Date of authorisation'
              detail={requestRun.authorisedAt
                ? moment(requestRun.authorisedAt).format('DD MMMM YYYY')
                  : '-'
              }
              className='u-font-size-larger slds-m-bottom_medium'
            />
          }
          {sortedContentEntries.length > 1
            ? <Grid
                items={sortedContentEntries.map(([field, value]) => (
                  <div className='slds-p-around_xx-small'>
                    <LabelledDetail
                      label={value.label}
                      detail={this.getReadOnlyDetailValue(field, value)}
                    />
                  </div>
                ))}
              />
            : <div className='slds-p-around_xx-small'>
                <LabelledDetail
                  label={sortedContentEntries[0][1].label}
                  detail={this.getReadOnlyDetailValue(sortedContentEntries[0][0], sortedContentEntries[0][1])}
                />
              </div>
          }
        </React.Fragment>
      )
    } else {
      return (
        <React.Fragment>
          <div className='slds-grid slds-m-bottom_xx-small' key={`requestCaptureForm_date-of-auth`}>
            {requestRun.requesttype.authorisationRequired &&
              <React.Fragment>
                <div
                  className='slds-m-top_xx-small u-font-size-larger slds-size_5-of-12'
                  title='When was the form signed or telephonically requested?'
                  style={{ cursor: 'help' }}>
                  Date of authorisation
                  <abbr className='slds-required slds-m-left_xx-small' title='required'>*</abbr>
                </div>
                <div className='slds-size_7-of-12'>
                  <DatePicker
                    selected={this.state.authorisedAt
                      ? moment(this.state.authorisedAt)
                      : null
                    }
                    onChange={this.setAuthorisedAtDate}
                    dateFormat='ll'
                    inline={true}
                    showMonthDropdown={true}
                    showYearDropdown={true}
                  />
                </div>
              </React.Fragment>
            }
          </div>
          {sortedContentEntries.map(([field, value]) => (
            <div className='slds-grid slds-m-bottom_xx-small' key={`requestCaptureForm_${field}`}>
              <div
                className='slds-m-top_xx-small u-font-size-larger slds-size_5-of-12'
                title={value.help_text}
                style={{ cursor: 'help' }}>
                {value.label}
                {value.label_requestrun_language && value[`label_${value.label_requestrun_language}`] &&
                  <div className='slds-text-body_small'>
                    {`(${value[`label_${value.label_requestrun_language}`]})`}
                  </div>
                }
                {value.required &&
                  <abbr className='slds-required slds-m-left_xx-small' title='required'>*</abbr>
                }
              </div>
              <div className='slds-size_7-of-12'>
                {this.getInput(field, value)}
              </div>
            </div>
          ))}
        </React.Fragment>
      )

    }
  }

  render() {
    let { requestRun, editAllowed } = this.props
    let formContent = Object.entries(this.state.content)
      .sort(([field1, value1], [field2, value2]) => (
        value1.order < value2.order
          ? -1
          : 1
      ))

    return (
      <div className={`slds-size_1-of-1 ${this.props.className}`}>
        {requestRun.requesttype
          ? <div className='slds-size_1-of-1 slds-p-bottom_medium slds-border_bottom'>
              {!requestRun.isCaptured &&
                <div className='slds-m-bottom_small'>
                  <abbr className='slds-required slds-m-left_xx-small' title='required'>*</abbr>
                  <span className='slds-text-color_weak slds-m-left_xx-small slds-text-body_small u-font-style-italic'>
                    Indicates required field
                  </span>
                </div>
              }
              {this.getForm(formContent, requestRun)}
              {this.state.captureComplete
                ? <div className='slds-m-bottom_medium slds-m-top_large'>
                    <SvgIcon
                      variant='utility'
                      symbol='check'
                      colour='success'
                      className='slds-m-right_medium'
                    />
                    <span className='u-font-size-large'>
                      Details successfully captured and saved
                    </span>
                    <br />
                    {editAllowed &&
                      (
                        (['ON_HOLD', 'PROCESSING', 'PROCESSED'].indexOf(requestRun.status) === -1 ||
                        (requestRun.workflowrunProcessing && requestRun.workflowrunProcessing.atStep === 2))
                      ) &&
                      <Button
                        type='brand'
                        onClick={() => this.setState({ captureComplete: false })}
                        minWidth={120}
                        className='slds-m-top_medium'>
                        Edit
                      </Button>
                    }
                  </div>
                : this.props.requestRun.status !== 'ON_HOLD' &&
                  <Mutation mutation={MUTATION_REQUESTRUN}>
                    {requestrunMutate => (
                      <div className='slds-m-top_large'>
                        <Button
                          type='brand'
                          onClick={() => {
                            this.setState({ saveInProgress: true })
                            this.checkAndTrimAnyCharFieldInputs()
                            this.updateRequestRun(false)
                          }}
                          minWidth={150}
                          disabled={!this.anyFieldChanged() || this.state.fileUploadInProgress}
                          className='slds-m-right_small'>
                          {this.state.saveInProgress
                            ? 'Saving...'
                            : 'Save'
                          }
                        </Button>
                        <Button
                          type='brand'
                          onClick={() => {
                            this.setState({ saveAndMarkCompleteInProgress: true})
                            this.checkAndTrimAnyCharFieldInputs()
                            this.updateRequestRun(true)
                          }}
                          minWidth={150}
                          disabled={!this.requiredFieldsBeenCaptured() || this.state.fileUploadInProgress}>
                          {this.state.saveAndMarkCompleteInProgress
                            ? 'Saving...'
                            : 'Save and mark captured'
                          }
                        </Button>
                      </div>
                    )}
                  </Mutation>
              }
            </div>
          : <div className='slds-grid slds-m-around_xx-large u-font-size-larger'>
              <SvgIcon
                variant='utility'
                symbol='warning'
                colour='warning'
                size='small'
                className='slds-m-right_medium'
              />
              No form data found.
            </div>
        }
      </div>
    )
  }
}

export default withRouter(RequestCaptureForm)



// WEBPACK FOOTER //
// ./src/components/request-capture-form/index.js